// Note Card Component with Grid/List View Support
import { useFolders } from '../../contexts/FoldersContext.jsx';
import { useTags } from '../../contexts/TagsContext.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useNotes } from '../../contexts/NotesContext.jsx';
import { toggleNoteFavorite, toggleNotePinned, softDeleteNote, restoreNote } from '../../services/firestore.js';
import Icon from '../common/Icon';
import ExportDropdown from '../common/ExportDropdown';
import './NoteCard.css';

export default function NoteCard({
    note,
    isSelected,
    isChecked,
    onSelect,
    onToggleCheck,
    viewMode = 'grid',
    onShare
}) {
    const { user } = useAuth();
    const { getFolderById } = useFolders();
    const { getTagById } = useTags();
    const { syncNotes } = useNotes();

    const folder = note.folderId ? getFolderById(note.folderId) : null;
    const noteTags = (note.tagIds || []).map(id => getTagById(id)).filter(Boolean);

    // Extract plain text from HTML content for preview
    const getPreviewText = (htmlContent) => {
        if (!htmlContent) return '';

        // If it's a string, try to parse as JSON (TipTap format)
        if (typeof htmlContent === 'string') {
            try {
                const parsed = JSON.parse(htmlContent);
                if (parsed.version === 2 && parsed.content) {
                    return extractPlainText(parsed.content);
                }
                if (parsed.type === 'doc') {
                    return extractPlainText(parsed);
                }
            } catch {
                // Plain text - strip HTML if present
                const div = document.createElement('div');
                div.innerHTML = htmlContent;
                return div.textContent?.substring(0, 200) || '';
            }
        }
        return '';
    };

    // Extract plain text from TipTap doc
    const extractPlainText = (doc) => {
        if (!doc || !doc.content) return '';

        let text = '';
        for (const node of doc.content) {
            text += extractTextFromNode(node) + ' ';
            if (text.length > 200) break;
        }
        return text.trim().substring(0, 200);
    };

    const extractTextFromNode = (node) => {
        if (node.type === 'text') {
            return node.text || '';
        }
        if (node.content) {
            return node.content.map(extractTextFromNode).join('');
        }
        return '';
    };

    // Format date
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
        });
    };

    // Quick action handlers
    const handleFavoriteToggle = async (e) => {
        e.stopPropagation();
        if (!user || !note.id) return;
        await toggleNoteFavorite(user.uid, note.id);
        syncNotes();
    };

    const handlePinToggle = async (e) => {
        e.stopPropagation();
        if (!user || !note.id) return;
        await toggleNotePinned(user.uid, note.id);
        syncNotes();
    };

    const handleDelete = async (e) => {
        e.stopPropagation();
        if (!user || !note.id) return;
        if (note.isDeleted) {
            await restoreNote(user.uid, note.id);
        } else {
            await softDeleteNote(user.uid, note.id);
        }
        syncNotes();
    };

    const handleCheckboxClick = (e) => {
        e.stopPropagation();
        onToggleCheck?.();
    };

    const handleShareClick = (e) => {
        e.stopPropagation();
        onShare?.();
    };

    // Handle URL click - open in new tab
    const handleUrlClick = (e) => {
        e.stopPropagation();
        const url = note.url || note.key;
        if (url && url.startsWith('http')) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    // Grid view card
    if (viewMode === 'grid') {
        return (
            <article
                className={`note-card grid-view ${isSelected ? 'selected' : ''} ${isChecked ? 'checked' : ''}`}
                onClick={onSelect}
            >
                {/* Status indicators */}
                <div className="status-indicators">
                    {note.isPinned && <span className="indicator pinned" title="Pinned"><Icon name="Pin" size="xs" /></span>}
                    {note.isFavorite && <span className="indicator favorite" title="Favorite"><Icon name="Star" size="xs" fill="currentColor" /></span>}
                    {note.isArchived && <span className="indicator archived" title="Archived"><Icon name="Archive" size="xs" /></span>}
                    {note.isDeleted && <span className="indicator deleted" title="Deleted"><Icon name="Trash2" size="xs" /></span>}
                </div>

                {/* Quick actions (show on hover) */}
                <div className="quick-actions">
                    <ExportDropdown note={note} placement="bottomRight" />
                    <button
                        onClick={handleShareClick}
                        className={note.isShared ? 'active' : ''}
                        title="Share note"
                    >
                        <Icon name="Link" size="sm" />
                    </button>
                    <button
                        onClick={handleFavoriteToggle}
                        className={note.isFavorite ? 'active' : ''}
                        title={note.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    >
                        <Icon name="Star" size="sm" fill={note.isFavorite ? "currentColor" : "none"} />
                    </button>
                    <button
                        onClick={handlePinToggle}
                        className={note.isPinned ? 'active' : ''}
                        title={note.isPinned ? 'Unpin' : 'Pin'}
                    >
                        <Icon name="Pin" size="sm" fill={note.isPinned ? "currentColor" : "none"} />
                    </button>
                    <button
                        onClick={handleDelete}
                        title={note.isDeleted ? 'Restore' : 'Delete'}
                        className={note.isDeleted ? 'restore' : 'delete'}
                    >
                        <Icon name={note.isDeleted ? "Undo" : "Trash2"} size="sm" />
                    </button>
                </div>

                {/* Card content */}
                <div className="card-content">
                    <h3 className="card-title">{note.title || getTitle(note)}</h3>
                    <p className="card-preview">{getPreviewText(note.content) || 'Empty note'}</p>
                </div>

                {/* Folder indicator */}
                {folder && (
                    <div className="card-folder" style={{ color: folder.color }}>
                        <span className="folder-icon"><Icon name={folder.icon ? "Folder" : "Folder"} size="xs" /></span>
                        <span className="folder-name">{folder.name}</span>
                    </div>
                )}

                {/* Tags */}
                {noteTags.length > 0 && (
                    <div className="card-tags">
                        {noteTags.slice(0, 3).map(tag => (
                            <span
                                key={tag.id}
                                className="tag-chip"
                                style={{
                                    backgroundColor: `${tag.color || '#6366f1'}22`,
                                    borderColor: tag.color || '#6366f1',
                                    color: tag.color || '#6366f1'
                                }}
                            >
                                {tag.name}
                            </span>
                        ))}
                        {noteTags.length > 3 && (
                            <span className="tag-more">+{noteTags.length - 3}</span>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div className="card-footer">
                    <div className="footer-meta-left">
                        {(note.url || note.key)?.startsWith('http') ? (
                            <a
                                href={note.url || note.key}
                                className="card-domain clickable"
                                title={`Open: ${note.url || note.key}`}
                                onClick={handleUrlClick}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Icon name="ExternalLink" size="xs" /> {note.domain || 'Open Link'}
                            </a>
                        ) : (
                            <span className="card-domain" title={note.url || note.key}>
                                <Icon name="Globe" size="xs" /> {note.domain || 'No URL'}
                            </span>
                        )}
                        {note.isShared && <span className="shared-badge">SHARED</span>}
                    </div>
                    <span className="card-date">{formatDate(note.updatedAt)}</span>
                </div>
            </article>
        );
    }

    // List view row
    return (
        <article
            className={`note-card list-view ${isSelected ? 'selected' : ''} ${isChecked ? 'checked' : ''}`}
            onClick={onSelect}
        >
            {/* Status indicators */}
            <div className="list-indicators">
                {note.isPinned && <span title="Pinned"><Icon name="Pin" size="xs" fill="currentColor" /></span>}
                {note.isFavorite && <span title="Favorite"><Icon name="Star" size="xs" fill="currentColor" /></span>}
            </div>

            {/* Title */}
            <div className="list-title">
                <span className="title-text">{note.title || getTitle(note)}</span>
                {note.isShared && <span className="shared-badge list-badge">SHARED</span>}
                {(note.isArchived || note.isDeleted) && (
                    <span className="status-badge">
                        <Icon name={note.isDeleted ? "Trash2" : "Archive"} size="xs" />
                    </span>
                )}
            </div>

            {/* Folder */}
            <div className="list-folder">
                {folder ? (
                    <span style={{ color: folder.color, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Icon name="Folder" size="xs" /> {folder.name}
                    </span>
                ) : (
                    <span className="no-folder">—</span>
                )}
            </div>

            {/* Tags */}
            <div className="list-tags">
                {noteTags.slice(0, 2).map(tag => (
                    <span
                        key={tag.id}
                        className="tag-dot"
                        style={{ backgroundColor: tag.color || '#6366f1' }}
                        title={tag.name}
                    />
                ))}
                {noteTags.length > 2 && (
                    <span className="tag-count">+{noteTags.length - 2}</span>
                )}
            </div>

            {/* Domain */}
            <div className="list-domain">
                {(note.url || note.key)?.startsWith('http') ? (
                    <a
                        href={note.url || note.key}
                        className="domain-link"
                        title={`Open: ${note.url || note.key}`}
                        onClick={handleUrlClick}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {note.domain || 'Open'}
                        <Icon name="ExternalLink" size="xs" />
                    </a>
                ) : (
                    note.domain || '—'
                )}
            </div>

            {/* Date */}
            <div className="list-date">
                {formatDate(note.updatedAt)}
            </div>

            {/* Quick actions */}
            <div className="list-actions">
                <ExportDropdown note={note} placement="bottomRight" />
                <button onClick={handleShareClick} title="Share">
                    <Icon name="Link" size="sm" />
                </button>
                <button onClick={handleFavoriteToggle} title="Favorite">
                    <Icon name="Star" size="sm" fill={note.isFavorite ? "currentColor" : "none"} />
                </button>
                <button onClick={handleDelete} title={note.isDeleted ? 'Restore' : 'Delete'}>
                    <Icon name={note.isDeleted ? "Undo" : "Trash2"} size="sm" />
                </button>
            </div>
        </article>
    );
}

// Helper to get title from note
function getTitle(note) {
    // Try to extract title from URL
    if (note.key && note.key.startsWith('http')) {
        try {
            const url = new URL(note.key);
            const path = url.pathname;
            if (path && path !== '/') {
                // Use last part of path as title
                const parts = path.split('/').filter(Boolean);
                if (parts.length > 0) {
                    return decodeURIComponent(parts[parts.length - 1])
                        .replace(/[-_]/g, ' ')
                        .replace(/\.(html?|php|aspx?)$/i, '');
                }
            }
            return url.hostname;
        } catch {
            return note.key;
        }
    }
    return note.domain || 'Untitled';
}